import type {
  Agent,
  AgentInstallation,
  CliPairingView,
  DashboardSession,
  DeviceView,
  EncryptedBlob,
  MessageView,
  MutableEncryptedBlob,
  ProjectAgentAssignment,
  ProjectCanvas,
  ProjectGraph,
  ProjectInvitation,
  ProjectInviteLink,
  ProjectInviteLinkCreated,
  ProjectInviteLinkPreview,
  ProjectSummary,
  ProjectWire,
  EligibleProjectCollaborator,
  RepositoryView,
  RuntimeIntent,
  Session,
  SkillSummary,
  SkillVersionView,
  TelegramLinkCreated,
  TelegramStatus,
  InvitationEmailReceipt,
  InvitationEmailStatus,
} from "@baton/contracts";

export interface CurrentUser { id: string; handle: string; generalAgentId: string | null }

/** Error codes the dashboard branches on; every other code is shown as the API's message. */
export const ApiErrorCodes = {
  /** 503: the relay is off on this server (no bot token, or no key vault). */
  telegramUnavailable: "telegram_unavailable",
  /** 409: another client saved the listening note first; reload before editing again. */
  listeningNoteConflict: "listening_note_conflict",
  generalAgentRequired: "general_agent_required",
  sessionRequired: "session_required",
} as const;
export type ApiErrorCode = typeof ApiErrorCodes[keyof typeof ApiErrorCodes];

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: unknown) {
    super(message);
    this.name = "ApiError";
  }

  is(code: ApiErrorCode): boolean { return this.code === code; }
}

export interface SendMessageInput {
  id: string;
  recipientAgentIds: string[];
  type: "chat" | "handoff" | "wip" | "report" | "activity";
  visibility: "participants" | "team";
  ciphertext: EncryptedBlob;
  replyToMessageId: string | null;
  repoId: string | null;
  projectId: string | null;
  idempotencyKey: string;
}

export class DashboardApi {
  readonly #baseUrl: URL;
  /** Direct API origin for bridges and WebSockets; ordinary HTTP stays on the dashboard proxy. */
  bridgeApiUrl: string;
  /** The dashboard's server-managed session id, held in memory only; set after `dashboardSession()`. */
  #sessionId: string | null = null;

  constructor(apiUrl: string, private readonly token: string) {
    this.#baseUrl = new URL(apiUrl);
    this.bridgeApiUrl = this.#baseUrl.origin;
  }

  eventUrl(path: string): URL {
    const url = new URL(path, this.bridgeApiUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url;
  }

  setSession(sessionId: string | null): void { this.#sessionId = sessionId; }
  get sessionId(): string | null { return this.#sessionId; }

  async me(): Promise<CurrentUser> {
    const raw = await this.request<{ id: string; handle: string; generalAgentId?: string | null }>("/v1/me");
    return { id: raw.id, handle: raw.handle, generalAgentId: raw.generalAgentId ?? null };
  }
  sessions(scope: "mine" | "team" = "mine") { return this.list<Session>(`/v1/sessions?scope=${scope}`, "sessions"); }
  agents(scope: "mine" | "team" = "mine") { return this.list<Agent>(`/v1/runtime/agents?scope=${scope}`, "agents"); }
  devices() { return this.list<DeviceView>("/v1/devices", "devices"); }
  installations() { return this.list<AgentInstallation>("/v1/agent-installations", "installations"); }
  feed(options: { projectId?: string; limit?: number; before?: string } = {}) {
    const query = new URLSearchParams({ limit: String(options.limit ?? 200) });
    if (options.projectId) query.set("projectId", options.projectId);
    if (options.before) query.set("before", options.before);
    return this.list<MessageView>(`/v1/feed?${query}`, "messages");
  }
  reports() { return this.list<MessageView>("/v1/reports?limit=200", "messages"); }
  message(messageId: string) { return this.request<MessageView>(`/v1/messages/${messageId}`); }
  skills() { return this.list<SkillSummary>("/v1/skills", "skills"); }
  skill(skillId: string) { return this.request<SkillVersionView>(`/v1/skills/${skillId}/latest`); }
  intents() { return this.list<RuntimeIntent>("/v1/intents", "intents"); }
  repositories() { return this.list<RepositoryView>("/v1/repositories", "repositories"); }
  projects() { return this.list<ProjectSummary>("/v1/projects", "projects"); }
  projectInvitations() { return this.list<ProjectInvitation>("/v1/project-invitations", "invitations"); }
  project(projectId: string) { return this.request<ProjectGraph>(`/v1/projects/${projectId}`); }
  eligibleProjectCollaborators(projectId: string) {
    return this.list<EligibleProjectCollaborator>(
      `/v1/projects/${projectId}/eligible-collaborators`,
      "collaborators",
    );
  }

  /** The one server-managed session the dashboard chats from; idempotent per user. */
  dashboardSession() {
    return this.request<DashboardSession>("/v1/dashboard/session", { method: "POST", body: "{}" });
  }

  /** Sends ciphertext produced by the crypto worker; the sender is the dashboard session's bound agent. */
  sendMessage(input: SendMessageInput) {
    return this.request<MessageView>("/v1/messages", {
      method: "POST",
      body: JSON.stringify({ ...input, attachmentIds: [], expiresAt: null }),
    }, { session: true });
  }

  createProject(input: {
    id: string;
    nameCiphertext: MutableEncryptedBlob;
    nameRevision: number;
    repositoryIds: string[];
    idempotencyKey: string;
  }) {
    return this.request<ProjectGraph>("/v1/projects", { method: "POST", body: JSON.stringify(input) });
  }

  addProjectRepository(projectId: string, repositoryId: string) {
    return this.request<ProjectGraph>(`/v1/projects/${projectId}/repositories`, {
      method: "POST",
      body: JSON.stringify({ repositoryId, idempotencyKey: crypto.randomUUID() }),
    });
  }

  removeProjectRepository(projectId: string, repositoryId: string) {
    return this.request<ProjectGraph>(`/v1/projects/${projectId}/repositories/${repositoryId}/remove`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  inviteProjectMember(projectId: string, userId: string, role: "admin" | "member") {
    return this.request<ProjectInvitation>(`/v1/projects/${projectId}/members/invite`, {
      method: "POST",
      body: JSON.stringify({ userId, role, idempotencyKey: crypto.randomUUID() }),
    });
  }

  acceptProjectInvitation(projectId: string) {
    return this.request<ProjectGraph>(`/v1/project-invitations/${projectId}/accept`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  invitationEmails(projectId: string) {
    return this.request<InvitationEmailStatus>(`/v1/projects/${projectId}/invitation-emails`);
  }

  emailProjectInvitation(projectId: string, userId: string, email: string, idempotencyKey: string) {
    return this.request<InvitationEmailReceipt>(`/v1/projects/${projectId}/members/${userId}/email`, {
      method: "POST", body: JSON.stringify({ email, idempotencyKey }),
    });
  }

  revokeProjectMember(projectId: string, userId: string) {
    return this.request<ProjectGraph>(`/v1/projects/${projectId}/members/${userId}/revoke`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  createInviteLink(projectId: string, input: { role: "admin" | "member"; expiresAt: string | null; maxUses: number | null }) {
    return this.request<ProjectInviteLinkCreated>(`/v1/projects/${projectId}/invite-links`, {
      method: "POST",
      body: JSON.stringify({ id: crypto.randomUUID(), ...input, idempotencyKey: crypto.randomUUID() }),
    });
  }

  listInviteLinks(projectId: string) {
    return this.list<ProjectInviteLink>(`/v1/projects/${projectId}/invite-links`, "inviteLinks");
  }

  revokeInviteLink(projectId: string, linkId: string) {
    return this.request<ProjectInviteLink>(`/v1/projects/${projectId}/invite-links/${linkId}/revoke`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  previewInviteLink(token: string) {
    return this.request<ProjectInviteLinkPreview>(`/v1/project-invite-links/${encodeURIComponent(token)}`);
  }

  acceptInviteLink(token: string) {
    return this.request<ProjectGraph>(`/v1/project-invite-links/${encodeURIComponent(token)}/accept`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  updateProjectMemberRole(projectId: string, userId: string, role: "member" | "admin") {
    return this.request<ProjectGraph>(`/v1/projects/${projectId}/members/${userId}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role, idempotencyKey: crypto.randomUUID() }),
    });
  }

  createAgent(input: { handle: string; role: string; icon?: string; color?: string }) {
    return this.request<Agent>("/v1/runtime/agents", {
      method: "POST",
      body: JSON.stringify({
        ...input,
        icon: input.icon ?? "agent",
        color: input.color ?? "#5f6fff",
        idempotencyKey: crypto.randomUUID(),
      }),
    });
  }

  assignProjectAgent(projectId: string, agentId: string, repositoryId: string | null) {
    return this.request<ProjectAgentAssignment>(`/v1/projects/${projectId}/agents`, {
      method: "POST",
      body: JSON.stringify({
        id: crypto.randomUUID(),
        agentId,
        repositoryId,
        idempotencyKey: crypto.randomUUID(),
      }),
    });
  }

  removeProjectAgent(projectId: string, assignmentId: string) {
    return this.request<ProjectGraph>(`/v1/projects/${projectId}/agents/${assignmentId}/remove`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  saveCanvasPositions(projectId: string, positions: Array<{ nodeId: string; x: number; y: number }>) {
    return this.request<ProjectCanvas>(`/v1/projects/${projectId}/canvas/positions`, {
      method: "PUT",
      body: JSON.stringify({ positions, idempotencyKey: crypto.randomUUID() }),
    });
  }

  createWire(projectId: string, wire: { id: string; sourceAgentId: string; targetAgentId: string }) {
    return this.request<ProjectWire>(`/v1/projects/${projectId}/wires`, {
      method: "POST",
      body: JSON.stringify({ ...wire, idempotencyKey: crypto.randomUUID() }),
    });
  }

  removeWire(projectId: string, wireId: string) {
    return this.request<ProjectGraph>(`/v1/projects/${projectId}/wires/${wireId}/remove`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
  }

  createIntent(input: {
    id: string;
    deviceId: string | null;
    agentId: string | null;
    kind: "initialize_agent" | "reconfigure_agent" | "install_skill" | "publish_skill";
    payloadCiphertext: EncryptedBlob;
    expiresAt: string;
    idempotencyKey: string;
  }) {
    return this.request<RuntimeIntent>("/v1/intents", { method: "POST", body: JSON.stringify(input) });
  }

  async eventTicket(afterId = 0) {
    return this.request<{ ticket: string; websocketPath: string; websocketProtocol: string; expiresAt: string }>(
      "/v1/events/ticket",
      { method: "POST", body: JSON.stringify({ afterId }) },
    );
  }

  async githubAvailability() {
    return this.request<{
      configured: boolean;
      setupUrl: string | null;
      teamKey: "managed" | "client";
      clientId?: string | null;
      installUrl?: string | null;
      apiUrl?: string | null;
    }>("/v1/auth/github/availability");
  }

  githubStatus() {
    return this.request<{ running: boolean; lastSuccessAt: string | null; lastErrorCode: string | null }>("/v1/github/status");
  }

  /** Asks the API to mirror GitHub now (collaborators become invitable once it finishes). */
  githubSync() {
    return this.request<{ accepted: boolean; running: boolean }>("/v1/github/sync", { method: "POST", body: "{}" });
  }

  /** Whether this server relays to Telegram and whether the signed-in member linked a chat. */
  telegramStatus() {
    return this.request<TelegramStatus>("/v1/telegram/status");
  }

  /**
   * Mints the single-use deep link; `acknowledged` is the consent click. The URL comes back
   * once, is rendered as a QR in this page, and is never sent anywhere else by the dashboard.
   */
  createTelegramLink(input: { acknowledged: true; idempotencyKey: string }) {
    return this.request<TelegramLinkCreated>("/v1/telegram/link", { method: "POST", body: JSON.stringify(input) });
  }

  unlinkTelegram() {
    return this.request<void>("/v1/telegram/link", { method: "DELETE" });
  }

  /** Replaces (or clears, with a null envelope) an owned agent's listening note; 409 when the revision is stale. */
  setListeningNote(agentId: string, input: { ciphertext: MutableEncryptedBlob | null; revision: number }) {
    return this.request<Agent>(`/v1/agents/${encodeURIComponent(agentId)}/listening-note`, {
      method: "PUT",
      body: JSON.stringify({ ...input, idempotencyKey: crypto.randomUUID() }),
    });
  }

  /** The API-managed team key; 404 when this API expects members to hold the key themselves. */
  async teamKey() {
    return this.request<{ keyId: string; keyEpoch: number; key: string }>("/v1/team-key");
  }

  /**
   * An invite token rides on the server-side OAuth state so the callback can admit and join the
   * visitor; `next` rides there too, so the browser comes back to the page that started the flow.
   * Neither ever appears in the URL sent to GitHub.
   */
  async githubStart(options: { inviteToken?: string | null; next?: string | null } = {}) {
    return this.request<{ authorizeUrl: string }>("/v1/auth/github/start", {
      method: "POST",
      body: JSON.stringify({
        ...(options.inviteToken ? { inviteToken: options.inviteToken } : {}),
        ...(options.next ? { next: options.next } : {}),
      }),
    });
  }

  /** Reads a pending terminal pairing. Reading never consumes the code. */
  readCliPairing(userCode: string) {
    return this.request<CliPairingView>(`/v1/auth/cli/pair/${encodeURIComponent(userCode)}`);
  }

  /** Mints the terminal's token under this account; the raw token exists only on that machine. */
  approveCliPairing(userCode: string) {
    return this.request<{ label: string; deviceId: string }>("/v1/auth/cli/pair/approve", {
      method: "POST",
      body: JSON.stringify({ userCode }),
    });
  }

  denyCliPairing(userCode: string) {
    return this.request<void>("/v1/auth/cli/pair/deny", { method: "POST", body: JSON.stringify({ userCode }) });
  }

  async bootstrapToken(input: {
    id: string;
    idempotencyKey: string;
    tokenDigest: string;
    encryptedLabel: null;
    labelRevision: 0;
    expiresAt: null;
  }) {
    return this.request<{ token: { id: string } }>("/v1/auth/bootstrap/token", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  private async list<T>(path: string, field: string): Promise<T[]> {
    const response = await this.request<Record<string, unknown>>(path);
    return response[field] as T[];
  }

  private async request<T>(path: string, init: RequestInit = {}, options: { session?: boolean } = {}): Promise<T> {
    if (options.session && !this.#sessionId) {
      throw new ApiError(401, "session_required", "The dashboard session is not ready yet");
    }
    const response = await fetch(new URL(path, this.#baseUrl), {
      ...init,
      headers: {
        ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...(options.session && this.#sessionId ? { "x-baton-session-id": this.#sessionId } : {}),
        ...init.headers,
      },
      cache: "no-store",
      credentials: "same-origin",
    });
    const body = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) {
      throw new ApiError(
        response.status,
        typeof body.error === "string" ? body.error : `http_${response.status}`,
        typeof body.message === "string" ? body.message : `HTTP ${response.status}`,
        body.details,
      );
    }
    return body as T;
  }
}
